import { useEffect, useState } from 'react';
import {
  IconPlus, IconPencil, IconKey, IconLock, IconAlertCircle, IconCheck,
  IconUserCheck, IconUserOff,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import { useAuth } from '../../hooks/useAuth';

/**
 * Phase 4.3 — Staff (user) management.
 *
 * ADMIN+ can:
 *   - Add staff (name + role + optional email + optional PIN)
 *   - Edit name / email / role / active
 *   - Reset PIN (returns plaintext once)
 *   - Set password (no plaintext return)
 *
 * OWNER role is reserved for OWNER actors. Lock-out prevention is
 * enforced server-side; UI mirrors the gate as a disabled control.
 */

type Role = 'CASHIER' | 'SUPERVISOR' | 'MANAGER' | 'ADMIN' | 'OWNER';
const ALL_ROLES: Role[] = ['CASHIER', 'SUPERVISOR', 'MANAGER', 'ADMIN', 'OWNER'];

const ROLE_TONE: Record<Role, string> = {
  CASHIER:    'bg-light text-heading border-border',
  SUPERVISOR: 'bg-info-soft text-info border-info/30',
  MANAGER:    'bg-warning-soft text-warning border-warning/30',
  ADMIN:      'bg-primary-soft text-primary border-primary/30',
  OWNER:      'bg-danger-soft text-danger border-danger/30',
};

interface ApiUser {
  id: string;
  name: string;
  role: Role;
  email: string | null;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export default function AdminStaffPage() {
  const { auth } = useAuth();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ApiUser | 'new' | null>(null);
  const [pinFlash, setPinFlash] = useState<{ name: string; pin: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/api/users', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setUsers(data.users ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <AdminLayout
      title="Staff"
      subtitle="Manage who can sign in at this branch"
      actions={
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
        >
          <IconPlus size={16} stroke={2.5} />
          New staff member
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-5">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-13 italic text-gray-700">No staff yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3 text-center">Status</th>
                  <th className="py-2 pr-3">Last login</th>
                  <th className="py-2 pr-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = auth?.userId === u.id;
                  return (
                    <tr key={u.id} className="border-t border-border-soft">
                      <td className="py-2.5 pr-3">
                        <p className="font-bold text-heading">{u.name}{isSelf && <span className="ml-2 text-12 font-medium text-gray-600">(you)</span>}</p>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`inline-flex items-center px-2 h-6 rounded-md border text-11 font-bold uppercase tracking-wider ${ROLE_TONE[u.role]}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700">{u.email ?? '—'}</td>
                      <td className="py-2.5 pr-3 text-center">
                        <span className={[
                          'inline-flex items-center gap-1 px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider',
                          u.active ? 'bg-success-soft text-success' : 'bg-light text-gray-700',
                        ].join(' ')}>
                          {u.active ? <IconUserCheck size={12} stroke={2.5} /> : <IconUserOff size={12} stroke={2.5} />}
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 tabular-nums">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-ZA') : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing(u)}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-primary hover:text-[#FFFFFF]"
                          title="Edit staff"
                        >
                          <IconPencil size={14} stroke={2.25} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <UserFormModal
        target={editing}
        actorRole={auth?.role as Role | undefined}
        actorId={auth?.userId}
        onClose={() => setEditing(null)}
        onSaved={(flash) => {
          if (flash) setPinFlash(flash);
          void load();
          setEditing(null);
        }}
      />

      <Modal open={pinFlash !== null} onClose={() => setPinFlash(null)} size="sm" hideClose>
        <div className="text-center py-3">
          <div className="w-14 h-14 rounded-full bg-warning-soft text-warning inline-flex items-center justify-center mb-3">
            <IconKey size={26} stroke={2} />
          </div>
          <h3 className="text-18 font-bold text-heading mb-1">PIN for {pinFlash?.name}</h3>
          <p className="text-13 font-medium text-gray-700 mb-4">Write this down — it's shown only once.</p>
          <p className="text-32 font-bold tabular-nums tracking-[0.3em] text-primary mb-5">{pinFlash?.pin}</p>
          <button
            type="button"
            onClick={() => setPinFlash(null)}
            className="h-11 px-5 inline-flex items-center rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover"
          >
            Got it
          </button>
        </div>
      </Modal>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// UserFormModal — create + edit + reset PIN + reset password
// ---------------------------------------------------------------------------

function UserFormModal({
  target, actorRole, actorId, onClose, onSaved,
}: {
  target: ApiUser | 'new' | null;
  actorRole: Role | undefined;
  actorId: string | undefined;
  onClose: () => void;
  onSaved: (flash?: { name: string; pin: string }) => void;
}) {
  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;
  const isSelf = existing?.id === actorId;
  const actorIsOwner = actorRole === 'OWNER';

  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('CASHIER');
  const [email, setEmail] = useState('');
  const [active, setActive] = useState(true);
  const [newPin, setNewPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setName(''); setRole('CASHIER'); setEmail(''); setActive(true);
      setNewPin(''); setNewPassword('');
    } else {
      setName(target.name); setRole(target.role); setEmail(target.email ?? '');
      setActive(target.active); setNewPin(''); setNewPassword('');
    }
    setErr(null);
  }, [target]);

  // Owner role only changeable by owner actor.
  const roleOptions = actorIsOwner ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'OWNER');
  // Disable role select if editing another OWNER and actor is not OWNER.
  const lockRole = !isNew && existing?.role === 'OWNER' && !actorIsOwner;

  async function save() {
    setSaving(true); setErr(null);
    try {
      if (isNew) {
        const r = await fetch('/api/users', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name, role, email: email || null,
            pin: newPin || undefined,
            password: newPassword || undefined,
          }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
        const data = await r.json();
        onSaved(data.generatedPin ? { name: data.user.name, pin: data.generatedPin } : undefined);
        return;
      }
      // Edit existing
      const r = await fetch(`/api/users/${existing!.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          role: lockRole ? undefined : role,
          email: email || null,
          active,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function resetPin() {
    if (!existing) return;
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`/api/users/${existing.id}/reset-pin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: newPin || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      const data = await r.json();
      onSaved({ name: existing.name, pin: data.newPin });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function resetPassword() {
    if (!existing) return;
    if (newPassword.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`/api/users/${existing.id}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} size="lg" title={isNew ? 'New staff member' : `Edit · ${existing?.name ?? ''}`}>
      <div className="space-y-4">
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Anees Dadabhay"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Role" required>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={lockRole}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading disabled:opacity-50"
            >
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Email" helper="optional · enables manager+ password login">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@habbyandlace.co.za"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </Field>
        </div>

        {!isNew && (
          <label className={[
            'flex items-center gap-2.5 p-3 rounded-md bg-light border border-border-soft',
            isSelf ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}>
            <input
              type="checkbox"
              checked={active}
              disabled={isSelf}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4"
            />
            <span>
              <span className="text-14 font-bold text-heading block">Account active</span>
              <span className="text-12 font-medium text-gray-700 block">
                {isSelf ? "You can't deactivate your own account." : 'Inactive accounts can\'t sign in.'}
              </span>
            </span>
          </label>
        )}

        <div className="rounded-lg bg-light border border-border-soft p-4">
          <div className="flex items-center gap-2 mb-3">
            <IconKey size={16} className="text-primary" />
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700">
              PIN {isNew ? '(optional — auto-generated if blank)' : '(reset)'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
              placeholder="4–8 digits"
              className="flex-1 h-11 px-3 rounded-md bg-surface border-2 border-border outline-none text-16 font-bold tabular-nums tracking-widest text-heading"
            />
            {!isNew && (
              <button
                type="button"
                onClick={resetPin}
                disabled={saving}
                className="h-11 px-4 inline-flex items-center gap-1.5 rounded-md bg-warning text-[#FFFFFF] text-13 font-bold hover:bg-warning-hover disabled:bg-gray-300"
              >
                Reset PIN
              </button>
            )}
          </div>
        </div>

        {!isNew && (
          <div className="rounded-lg bg-light border border-border-soft p-4">
            <div className="flex items-center gap-2 mb-3">
              <IconLock size={16} className="text-primary" />
              <p className="text-13 font-bold uppercase tracking-wider text-gray-700">Password (reset)</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="flex-1 h-11 px-3 rounded-md bg-surface border-2 border-border outline-none text-15 font-semibold text-heading"
              />
              <button
                type="button"
                onClick={resetPassword}
                disabled={saving || newPassword.length < 8}
                className="h-11 px-4 inline-flex items-center gap-1.5 rounded-md bg-warning text-[#FFFFFF] text-13 font-bold hover:bg-warning-hover disabled:bg-gray-300"
              >
                Set password
              </button>
            </div>
          </div>
        )}

        {err && <p className="text-13 font-semibold text-danger">{err}</p>}

        <div className="flex items-stretch gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !name.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : (isNew ? 'Create staff member' : 'Save changes')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, helper, required, children }: { label: string; helper?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}{required && <span className="text-danger ml-1">*</span>}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      {children}
    </label>
  );
}
